import React, { useEffect, useRef, useState, useCallback } from 'react'
import { widget, LanguageCode } from '../../charting_library'

// 获取语言设置的函数
const getLanguageFromURL = (): LanguageCode => {
  const regex = new RegExp('[\\?&]lang=([^&#]*)')
  const results = regex.exec(window.location.search)
  return (results ? decodeURIComponent(results[1].replace(/\+/g, ' ')) : 'en') as LanguageCode
}

// 分辨率选项
const RESOLUTIONS = [
  { label: '1秒', value: '1s', tvValue: '1S', apiType: '1s', timeSpanDays: 3 },
  { label: '5分钟', value: '5m', tvValue: '5', apiType: '5m', timeSpanDays: 3 },
  { label: '15分钟', value: '15m', tvValue: '15', apiType: '15m', timeSpanDays: 3 },
  { label: '1小时', value: '1h', tvValue: '60', apiType: '15m', timeSpanDays: 7 },
  { label: '4小时', value: '4h', tvValue: '240', apiType: '15m', timeSpanDays: 30 },
  { label: '1天', value: '1d', tvValue: '1D', apiType: '15m', timeSpanDays: 90 }
]

// API 基础配置
const BASE_API_URL = 'https://birdeye-proxy.raydium.io/defi/ohlcv/base_quote'
const BASE_ADDRESS = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
const QUOTE_ADDRESS = 'So11111111111111111111111111111111111111112'
const DEFAULT_TIME_TO = Math.floor(Date.now() / 1000) // 动态获取当前时间戳（秒）
const LOAD_MORE_DAYS = 3 // 每次加载更多数据的默认天数
const MAX_BARS = 5000 // 最大 K 线条数
const DEBOUNCE_MS = 500 // 防抖时间（毫秒）
const MIN_TIME = 1609459200 // 2021-01-01，防止请求过早数据

// 接口定义 API 返回的数据结构
interface OHLCVItem {
  o: number
  h: number
  l: number
  c: number
  vBase: number
  vQuote: number
  unixTime: number
}

interface APIData {
  success: boolean
  data: {
    baseAddress: string
    quoteAddress: string
    type: string
    items: OHLCVItem[]
  }
}

// TradingView 所需的 K 线格式
interface Bar {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

// 聚合 15分钟数据为目标分辨率
const aggregateBars = (bars: Bar[], resolution: string): Bar[] => {
  if (resolution === '1s' || resolution === '5m' || resolution === '15m') {
    return bars
  }

  const intervalSeconds = {
    '1h': 3600,
    '4h': 14400,
    '1d': 86400
  }[resolution]

  if (!intervalSeconds) return bars

  const aggregated: Bar[] = []
  const intervalMs = intervalSeconds * 1000

  let currentWindow: Bar | null = null

  for (const bar of bars) {
    const windowStart = Math.floor(bar.time / intervalMs) * intervalMs
    if (!currentWindow || windowStart > currentWindow.time) {
      if (currentWindow) {
        aggregated.push(currentWindow)
      }
      currentWindow = {
        time: windowStart,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.volume
      }
    } else {
      currentWindow.high = Math.max(currentWindow.high, bar.high)
      currentWindow.low = Math.min(currentWindow.low, bar.low)
      currentWindow.close = bar.close
      currentWindow.volume += bar.volume
    }
  }

  if (currentWindow) {
    aggregated.push(currentWindow)
  }

  return aggregated
}

// 数据源实现
const SimpleDatafeed = (bars: Bar[], resolution: string) => ({
  onReady: (callback: any) => {
    console.log('数据源准备就绪')
    callback({
      supports_search: true,
      supports_group_request: false,
      supported_resolutions: RESOLUTIONS.map(r => r.tvValue)
    })
  },

  resolveSymbol: (symbolName: string, onSymbolResolvedCallback: any, onResolveErrorCallback: any) => {
    console.log('解析符号:', symbolName)
    onSymbolResolvedCallback({
      name: symbolName,
      full_name: `Custom:${symbolName}`,
      type: 'crypto',
      session: '24x7',
      timezone: 'Etc/UTC',
      ticker: symbolName,
      minmov: 1,
      pricescale: 1000000,
      has_intraday: true,
      has_daily: true,
      has_weekly_and_monthly: true,
      supported_resolutions: RESOLUTIONS.map(r => r.tvValue),
      volume_precision: 2
    })
  },

  getBars: (symbolInfo: any, resolution: any, periodParams: any, onHistoryCallback: any, onErrorCallback: any) => {
    const { from, to } = periodParams
    console.log('获取K线数据:', symbolInfo, resolution, from, to)
    const mappedResolution = RESOLUTIONS.find(r => r.tvValue === resolution)?.value || '15m'
    const aggregatedBars = aggregateBars(bars, mappedResolution)
    const filteredBars = aggregatedBars.filter(bar => bar.time >= from * 1000 && bar.time <= to * 1000)
    console.log('过滤后的数据条数:', filteredBars.length, '数据:', filteredBars)
    if (filteredBars.length) {
      onHistoryCallback(filteredBars, { noData: false })
    } else {
      onHistoryCallback([], { noData: true })
    }
  },

  subscribeBars: (
    symbolInfo: any,
    resolution: any,
    onRealtimeCallback: any,
    subscribeUID: any,
    onResetCacheNeededCallback: any
  ) => {
    console.log('订阅实时数据（未实现）')
  },

  unsubscribeBars: (subscriberUID: any) => {
    console.log('取消订阅实时数据（未实现）')
  }
})

// 防抖函数
const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// 图表组件
export default function TVChartContainer() {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<any>(null)
  const [bars, setBars] = useState<Bar[]>([])
  const [resolution, setResolution] = useState(RESOLUTIONS[2].value) // 默认 15m
  const [isLoading, setIsLoading] = useState(false)
  const [earliestTime, setEarliestTime] = useState(DEFAULT_TIME_TO)
  const isFetchingRef = useRef(false) // 防止重复请求
  const isInitialLoadRef = useRef(true) // 标记是否为初始加载

  // 获取 API 数据
  const fetchData = async (timeFrom: number, timeTo: number, apiType: string) => {
    if (isFetchingRef.current) {
      return []
    }
    isFetchingRef.current = true
    setIsLoading(true)
    const apiUrl = `${BASE_API_URL}?base_address=${BASE_ADDRESS}&quote_address=${QUOTE_ADDRESS}&type=${apiType}&time_from=${timeFrom}&time_to=${timeTo}`
    try {
      console.log('发送 API 请求:', apiUrl)
      const response = await fetch(apiUrl)
      const result: APIData = await response.json()
      console.log(result);
      
      if (result.success) {
        const newBars: Bar[] = result.data.items.map(item => ({
          time: item.unixTime * 1000,
          open: item.o,
          high: item.h,
          low: item.l,
          close: item.c,
          volume: 0
        }))
        console.log(`从API获取的数据 (${resolution}, ${timeFrom}-${timeTo}):`, newBars)
        return newBars
      } else {
        console.error('API返回数据无效:', result)
        return []
      }
    } catch (error) {
      console.error('获取API数据失败:', error)
      return []
    } finally {
      setIsLoading(false)
      isFetchingRef.current = false
    }
  }

  // 初始数据加载
  useEffect(() => {
    const loadInitialData = async () => {
      const selectedRes = RESOLUTIONS.find(r => r.value === resolution)
      if (!selectedRes) return

      const timeSpanSeconds = selectedRes.timeSpanDays * 24 * 60 * 60
      const timeFrom = DEFAULT_TIME_TO - timeSpanSeconds

      console.log('加载初始数据:', { timeFrom, timeTo: DEFAULT_TIME_TO, apiType: selectedRes.apiType })
      const newBars = await fetchData(timeFrom, DEFAULT_TIME_TO, selectedRes.apiType)
      setBars(newBars)
      if (newBars.length > 0) {
        setEarliestTime(Math.min(...newBars.map(bar => bar.time / 1000)))
      }
      isInitialLoadRef.current = false // 标记初始加载完成
    }
    loadInitialData()
  }, [resolution])

  // 加载更多数据（防抖）
  const loadMoreData = useCallback(
    debounce(async () => {
      if (isFetchingRef.current) {
        console.log('已有正在进行的请求，跳过 loadMoreData')
        return
      }

      const selectedRes = RESOLUTIONS.find(r => r.value === resolution)
      if (!selectedRes) {
        console.error('未找到分辨率:', resolution)
        return
      }

      const timeSpanSeconds = LOAD_MORE_DAYS * 24 * 60 * 60
      const newTimeTo = earliestTime
      const newTimeFrom = newTimeTo - timeSpanSeconds

      // 防止请求过早的数据
      if (newTimeFrom < MIN_TIME) {
        console.log('已达到最早时间限制:', newTimeFrom)
        return
      }

      // 检查是否需要加载（避免重复加载相同时间范围）
      const firstBarTime = bars.length > 0 ? bars[0].time / 1000 : newTimeTo
      if (newTimeTo >= firstBarTime) {
        console.log('无需加载，数据已包含该时间范围:', { newTimeTo, firstBarTime })
        return
      }

      console.log('触发左拉请求:', { newTimeFrom, newTimeTo, apiType: selectedRes.apiType })
      const newBars = await fetchData(newTimeFrom, newTimeTo, selectedRes.apiType)
      if (newBars.length > 0) {
        const allBars = [...newBars, ...bars].sort((a, b) => a.time - b.time)
        const uniqueBars = Array.from(new Map(allBars.map(bar => [bar.time, bar])).values())
        const trimmedBars = uniqueBars.slice(-MAX_BARS)
        setBars(trimmedBars)
        const newEarliestTime = Math.min(...trimmedBars.map(bar => bar.time / 1000))
        setEarliestTime(newEarliestTime)
        if (chartInstance.current) {
          console.log('重置图表数据，当前K线条数:', trimmedBars.length)
          chartInstance.current.resetData()
        }
      } else {
        console.log('未获取到新数据')
      }
    }, DEBOUNCE_MS),
    [bars, earliestTime, resolution]
  )

  // 初始化 TradingView 图表并监听时间范围变化
  useEffect(() => {
    let isMounted = true
    if (!chartRef.current || bars.length === 0) {
      console.log('图表未初始化或无数据，跳过:', { chartRef: !!chartRef.current, barsLength: bars.length })
      return
    }

    const selectedResolution = RESOLUTIONS.find(r => r.value === resolution)?.tvValue || '15'
    const tvWidget = new widget({
      symbol: 'RAY/SOL',
      interval: selectedResolution,
      container: chartRef.current,
      datafeed: SimpleDatafeed(bars, resolution),
      library_path: '/charting_library/',
      locale: getLanguageFromURL(),
      disabled_features: [
        'header_symbol_search',
        'header_compare',
        'header_indicators',
        'timeframes_toolbar',
        'header_resolutions',
        'display_market_status',
        'header_quick_search',
        'symbol_search_hot_key',
        'auto_enable_symbol_labels',
        'compare_symbol_search_spread_operators',
        'allow_arbitrary_symbol_search_input',
        'create_volume_indicator_by_default',
        'volume_force_overlay'
      ],
      enabled_features: ['study_templates'],
      charts_storage_url: 'https://saveload.tradingview.com',
      charts_storage_api_version: '1.1',
      client_id: 'tradingview.com',
      user_id: 'public_user_id',
      fullscreen: false,
      autosize: true,
      studies_overrides: {},
      debug: false
    })

    tvWidget.onChartReady(() => {
      if (!isMounted) return
      const chart = tvWidget.activeChart()
      chartInstance.current = chart
      if (chart && bars.length > 0) {
        const aggregatedBars = aggregateBars(bars, resolution)
        if (aggregatedBars.length > 0) {
          chart
            .setVisibleRange({
              from: aggregatedBars[0].time / 1000,
              to: aggregatedBars[aggregatedBars.length - 1].time / 1000
            })
            .then(() => {
              chart.resetData()
            })
        }

        let initialRangeSet = false // 标记是否已设置初始范围

        chart.onVisibleRangeChanged().subscribe(null, (range: any) => {
          if (!isMounted || isFetchingRef.current || bars.length === 0) {
            console.log('跳过VisibleRangeChanged:', {
              isMounted,
              isFetching: isFetchingRef.current,
              barsLength: bars.length
            })
            return
          }

          const { from } = range
          const selectedRes = RESOLUTIONS.find(r => r.value === resolution)
          if (!selectedRes) {
            console.log('未找到分辨率:', { resolution })
            return
          }

          const firstBarTime = bars[0].time / 1000
          // 仅当接近最早 K 线（1 天范围内）时触发加载
          const preloadThreshold = firstBarTime + 24 * 60 * 60

          if (!initialRangeSet) {
            console.log('首次VisibleRangeChange，跳过')
            initialRangeSet = true
            return
          }

          // console.log('VisibleRangeChanged:', { from, preloadThreshold, firstBarTime })
          if (from <= preloadThreshold) {
            // console.log('检测到左拉到底，触发加载:', { from, preloadThreshold, firstBarTime });
            // loadMoreData();
          } else {
            // console.log('未达到左拉到底条件:', { from, preloadThreshold, firstBarTime });
          }
        })

        chart.onIntervalChanged().subscribe(null, (interval: string) => {
          if (!isMounted) return
          const selectedRes = RESOLUTIONS.find(r => r.tvValue === interval)
          if (selectedRes && selectedRes.value !== resolution) {
            console.log('分辨率变化:', interval, '->', selectedRes.value)
            setResolution(selectedRes.value)
          }
        })

        tvWidget.headerReady().then(() => {
          if (!isMounted) return
          const button = tvWidget.createButton()
          button.setAttribute('title', '点击显示通知弹窗')
          button.classList.add('apply-common-tooltip')
          button.addEventListener('click', () => {
            tvWidget.showNoticeDialog({
              title: '通知',
              body: 'TradingView 图表库 API 正常工作',
              callback: () => console.log('已通知！')
            })
          })
          button.innerHTML = '检查 API'
        })
      }
    })

    return () => {
      isMounted = false
      tvWidget.remove()
    }
  }, [bars, resolution, loadMoreData])

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      {/* 图表容器 */}
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
      {/* 加载指示器 */}
      {isLoading && (
        <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10 }}>
          <span>加载中...</span>
        </div>
      )}
    </div>
  )
}
